import { useMemo } from "react"

import { IHomeFilterBody } from "src/data/homes/types/homeQueryTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHomesInfinite } from "src/data/organizations/queries/homeQueries"
import { useFetchTasks } from "src/data/taskManagement/taskManagementQueries"
import { TTaskFetchFilters } from "src/data/taskManagement/taskManagementTypes"
import { unique } from "src/utils/genericUtil"

export function useFetchSchedulerData({
  homeFilters,
  taskFilters,
}: {
  homeFilters: IHomeFilterBody
  taskFilters: TTaskFetchFilters
}) {
  const { orgId } = useOrganization()

  const fetchHomes = useFetchHomesInfinite({
    orgId,
    filters: { ...homeFilters },
  })

  const homes = useMemo(() => {
    if (!fetchHomes.data?.pages) {
      return []
    }

    return fetchHomes.data.pages.flatMap((page) => page.homes ?? [])
  }, [fetchHomes.data?.pages])

  const homeIds = useMemo(() => {
    return unique(homes.map((home) => home.home_id)).filter(Boolean)
  }, [homes])

  const fetchTasks = useFetchTasks({
    orgId,
    filters: {
      ...taskFilters,
      homegroup_ids: homeFilters.homegroup_ids?.split(","),
      home_ids: homeIds,
    },
    options: {
      enabled: homeIds.length > 0,
    },
  })

  const tasks = fetchTasks.data?.tasks ?? []

  const loading =
    fetchHomes.isLoading ||
    fetchHomes.isFetchingNextPage ||
    fetchTasks.isLoading

  const error = fetchHomes.isError || fetchTasks.isError

  function loadMore() {
    if (fetchHomes.hasNextPage && !fetchHomes.isFetchingNextPage) {
      fetchHomes.fetchNextPage()
    }
  }

  return {
    loading,
    homes,
    tasks,
    error,
    hasNextPage: fetchHomes.hasNextPage,
    loadMore,
    isFetchingNextPage: fetchHomes.isFetchingNextPage,
  }
}
